import { useState } from 'react';
import { BaseModal } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useCandidatoMutations } from '../../../hooks/data';

interface AdicionarCandidatoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Cadastro mínimo do candidato — só nome/e-mail (o resto é preenchido pelo próprio
 * candidato via link, ver decisão registrada: sem e-mail não tem como entregar o link) e
 * se esse candidato vê as perguntas sobre uso de IA (nem toda vaga precisa). Não dispara
 * e-mail nenhum aqui — o recrutador decide quando/como entregar o link depois, na lista
 * (copiar link ou enviar por e-mail), já que nem todo candidato tem facilidade de acessar
 * e-mail (decisão registrada: link direto tem mais adesão).
 */
export default function AdicionarCandidatoModal({ isOpen, onClose }: AdicionarCandidatoModalProps) {
  const { user } = useAuth();
  const { criarCandidato } = useCandidatoMutations();

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [perguntasSobreIA, setPerguntasSobreIA] = useState(false);

  const podeSubmeter = Boolean(nome.trim() && email.trim());
  const enviando = criarCandidato.isPending;

  const handleClose = () => {
    if (enviando) return;
    setNome('');
    setEmail('');
    setPerguntasSobreIA(false);
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!podeSubmeter || !user) return;

    await criarCandidato.mutateAsync({
      nome: nome.trim(),
      email: email.trim(),
      perguntasSobreIA,
      createdBy: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' },
    });

    setNome('');
    setEmail('');
    setPerguntasSobreIA(false);
    onClose();
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Adicionar Candidato" maxWidth="max-w-md" fullHeight={false}>
      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Nome *</label>
          <input
            type="text"
            className="input-primary mt-1 w-full"
            value={nome}
            onChange={(event) => setNome(event.target.value)}
            placeholder="Nome do candidato (pode ser parcial)"
            disabled={enviando}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">E-mail *</label>
          <input
            type="email"
            className="input-primary mt-1 w-full"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="E-mail do candidato"
            disabled={enviando}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={perguntasSobreIA}
            onChange={(event) => setPerguntasSobreIA(event.target.checked)}
            disabled={enviando}
          />
          Incluir perguntas sobre uso de IA no formulário
        </label>
        <p className="text-xs text-gray-500">
          O link de preenchimento é gerado ao salvar — copie ou envie por e-mail depois, na lista.
        </p>
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={enviando}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={!podeSubmeter || enviando}>
            {enviando ? 'Adicionando...' : 'Adicionar Candidato'}
          </button>
        </div>
      </form>
    </BaseModal>
  );
}
